import {
  Avatar,
  AvatarSize,
  Box,
  Icon,
  IconSize,
  SideSheet,
  SideSheetClose,
  SideSheetHeader,
  SideSheetSize,
  SideSheetSubHeader,
  Spinner,
  SpinnerSize,
  Text,
  theme,
} from '@bechtel/gds';
import { CommodityStatusCode, PersonaType, Weld } from '@bechtel/types';
import { find, map, orderBy } from 'lodash';
import React, { FC, Fragment, Suspense, useState } from 'react';
import { useName, TitleValue, SuspenseLoader, ErrorBoundary } from '@bechtel/client-common';
import { useCommodity } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import { useWeldStatusText } from '../../weld/hooks';
import {
  WeldAddCrewStyled,
  WeldSideSheetContentColStyled,
  WeldDetailStyled,
  WeldoutItemStyled,
  WeldDetailSideSheetBodyStyled,
  WeldDetailSideSheetContentColStyled,
} from './styled';
import { WeldDetailsProps } from './types';
import AssociatedWeldDetail from './associated-weld-detail/internals';
import { WeldClaim } from '@bechtel/client-weld-common';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const WeldStatus = ({ weld }) => {
  const statusText = useWeldStatusText(weld);
  return (
    <Text size="300" color="grey.600">
      {statusText}
    </Text>
  );
};

const WeldDetails: FC<WeldDetailsProps> = ({ close, weldList }) => {
  const { i18n } = useLingui();
  const { getFullName } = useName();
  const { tag } = useWorkPackage();
  const { id: commodityId, typeName, tag: commodityTag } = useCommodity();
  const [selectedWeld, setSelectedWeld] = useState<Weld>(weldList[0]);
  const [isClaim, setIsClaim] = useState<boolean>(false);
  const assignedStatusText = t`Assigned`;
  const unAssignedStatusText = t`UnAssigned`;
  const orderByWeldStatusList = orderBy(
    weldList,
    (weld) =>
      weld?.status?.status ||
      (find(weld.commodityAssignees, (item) => {
        return item.personaType === PersonaType.FOREMAN;
      })
        ? assignedStatusText
        : unAssignedStatusText)
  );
  return (
    <>
      {!isClaim ? (
        <SideSheet onDismiss={close} size={SideSheetSize.MEDIUM} visible>
          <SideSheetHeader title={t`Weldout Details`}>
            <SideSheetClose onClick={close}></SideSheetClose>
          </SideSheetHeader>
          <SideSheetSubHeader>
            <Box mb={{ sm: '200' }} data-testid="commodity-header">
              <TitleValue title={t`Work Package`} value={tag} size="500" />
            </Box>
            <Box>
              <TitleValue title={typeName} value={commodityTag} size="500" />
            </Box>
          </SideSheetSubHeader>
          <WeldDetailSideSheetBodyStyled>
            <WeldDetailSideSheetContentColStyled>
              <Box
                pb={{ sm: '550' }}
                pt={{ sm: '600' }}
                px={{ sm: '500' }}
                css={`
                  background-color: ${({ theme }) => theme.colors.grey[200]};
                `}
              >
                <Text family="graphik" size="400" weight="600" color="grey.1000">
                  <Trans> Select a Weldout</Trans>
                </Text>
              </Box>
              {map(orderByWeldStatusList, (weld) => {
                const commodityAssignee = find(weld.commodityAssignees, (item) => {
                  return item.personaType == PersonaType.FOREMAN;
                });
                const isReadyToWeld = weld.status?.status === CommodityStatusCode.READY_TO_WELD;
                const assignedWeldForeman = !isReadyToWeld ? weld.status?.statusBy : commodityAssignee?.assignee;
                return (
                  <Fragment key={weld.tag}>
                    <WeldDetailStyled isActive={selectedWeld.id == weld.id} onClick={() => setSelectedWeld(weld)}>
                      <Box pt={{ sm: '400' }} pb={{ sm: '100' }}>
                        <Text size="400" weight="600" color="grey.1000">
                          {weld.tag}
                        </Text>
                      </Box>
                      <Box pt={{ sm: '300' }} pb={{ sm: '100' }} justifyContent={{ sm: 'space-between' }}>
                        <WeldStatus weld={weld}></WeldStatus>
                        <Icon fillColor="blue.800" size={IconSize.MEDIUM} icon="chevron-right" />
                      </Box>
                      <Box pt={{ sm: '450' }}>
                        <WeldoutItemStyled
                          status={weld?.status?.status}
                          isAssigned={commodityAssignee?.assignee ? true : false}
                        />

                        {assignedWeldForeman && (
                          <Avatar
                            style={{ marginRight: `${theme.spacings[450]}` }}
                            src={assignedWeldForeman.imageUrl}
                            size={AvatarSize.MEDIUM}
                          />
                        )}
                        {!assignedWeldForeman && (
                          <WeldAddCrewStyled>
                            <Icon size={IconSize.MEDIUM} icon="user" fillColor="blue.800" />
                          </WeldAddCrewStyled>
                        )}

                        {assignedWeldForeman && (
                          <Box flexDirection={{ sm: 'column' }}>
                            <Box pb={{ sm: '100' }}>
                              <Text size="400" color="grey.1000">
                                {getFullName(assignedWeldForeman.firstName, assignedWeldForeman.lastName)}
                              </Text>
                            </Box>
                            <Box pt={{ sm: '200' }} pb={{ sm: '100' }}>
                              <Text size="300" color="grey.600">
                                {i18n._(assignedWeldForeman.persona)}
                              </Text>
                            </Box>
                          </Box>
                        )}
                        {!assignedWeldForeman && (
                          <Box flexDirection={{ sm: 'column' }} justifyContent={{ sm: 'center' }}>
                            <Text size="400" color="grey.1000">
                              <Trans> Unassigned</Trans>
                            </Text>
                          </Box>
                        )}
                      </Box>
                    </WeldDetailStyled>
                  </Fragment>
                );
              })}
            </WeldDetailSideSheetContentColStyled>

            <WeldSideSheetContentColStyled>
              <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                <AssociatedWeldDetail
                  commodityId={commodityId}
                  selectedWeld={selectedWeld}
                  onClaim={() => {
                    setIsClaim(true);
                  }}
                />
              </Suspense>
            </WeldSideSheetContentColStyled>
          </WeldDetailSideSheetBodyStyled>
        </SideSheet>
      ) : (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldClaim close={close} weld={selectedWeld} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export default WeldDetails;
